export class OtcHelper {
    /**
     * Returns OTC timing info:
     * - If otc.starts_at is in the future: returns "Scheduled for DD Mon YYYY at HH:MM AM/PM"
     * - Else if otc.expires_at is in the future: returns remaining time in days/hours/minutes
     * - Else: returns "Expired"
     * @param {Object} otc - OTC object with starts_at and expires_at fields
     * @returns {string} Human-readable time info
     */
    static getRemainingTime(otc) {
        const now = Date.now();

        const startsAt =
            otc.starts_at._seconds * 1000 + Math.floor(otc.starts_at._nanoseconds / 1e6);
        const expiresAt =
            otc.expires_at._seconds * 1000 +
            Math.floor(otc.expires_at._nanoseconds / 1e6);

        // Case 1: Not active yet
        if (startsAt > now) {
            const date = new Date(startsAt);
            const datePart = date.toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
            const timePart = date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            return `Scheduled for ${datePart} at ${timePart}`;
        }

        // Case 2: Already active, check if expired
        const remainingMs = expiresAt - now;

        if (remainingMs <= 0) {
            return 'Expired';
        }

        const minutes = Math.floor(remainingMs / (1000 * 60)) % 60;
        const hours = Math.floor(remainingMs / (1000 * 60 * 60)) % 24;
        const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));

        const parts = [];
        if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') + ' remaining' : 'Expired';
    }

    /**
     * Formats a Unix timestamp into a human-readable string in English.
     * Example: "Tuesday, July 2, 2025 at 10:45 AM GMT+2"
     * @param {number} timestamp - Unix timestamp in seconds
     * @param {boolean} [includeTimeZone=false] - Whether to include the time zone in the output
     * @returns {string} Formatted date and time string
     */
    static formatTimestamp(timestamp, includeTimeZone = false) {
        const date = new Date(timestamp * 1000);

        const options = {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };

        if (includeTimeZone) {
            options.timeZoneName = 'short';
        }

        return new Intl.DateTimeFormat('en-US', options).format(date);
    }

    /**
     * Converts an ISO 8601 string to a Firestore Timestamp object format.
     * Useful when preparing data for Firestore or Firestore-emulated APIs.
     *
     * @param {string} isoString - Date string in ISO format (e.g. "2025-07-22T22:00:00.000Z")
     * @returns {{ _seconds: number, _nanoseconds: number }} Firestore timestamp format
     */
    static toFirestoreTimestamp(isoString) {
        const date = new Date(isoString);
        return {
            _seconds: Math.floor(date.getTime() / 1000),
            _nanoseconds: (date.getTime() % 1000) * 1e6
        };
    }
}
